import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import { ensureOfflineReady } from '../../offline/initOffline'
import { getOfflineDb } from '../../offline/dbFactory'
import { feedLogApi } from '../../utils/feedLogApi'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import { loadWaterTableRows } from '../../utils/waterTable'
import OperatorGate from './OperatorGate'
import CrewModeFarmBar from './CrewModeFarmBar'
import { useCrewModeFarm } from './useCrewModeFarm'
import { enabledCrewModeStreams } from './crewModeStreams'
import { syncCrewModeFlagsForFarm } from './crewModeFlagSync'
import { recordCrewEvent } from './crewModeTelemetry'
import {
  buildOperatorTodayEntries,
  type CrewTodayEntry,
  type CrewTodaySyncState,
  type OperatorTodayEntriesResult
} from './operatorTodayEntries'
import { farmTimeZone } from '../../utils/farmTime'

// Crew Mode pilot shell v0 — bundle A (home / task launcher) + bundle E
// (operator "my entries today") + bundle G (waiting-to-sync count).
//
// The four `/crew/*` capture routes exist with no index and no nav
// between them (strategy §9 "¿Qué vas a registrar hoy?"). This is that
// index: a launcher, mounted inside `OperatorGate` so the active-operator
// badge + switch-operator control are present, with one large icon+text
// button per stream that is flag-enabled for the active farm; below it,
// the active operator's own entries from today with each entry's sync
// state, for self-verification before leaving the yard (strategy §10 P1).
//
// Charter O-5: renders fully offline — the launcher reads only the four
// local flag values, and the "today" list reads local-first (the
// `xLogApi.list` calls fall back to the Dexie cache when offline;
// `loadWaterTableRows` is localStorage). No call blocks the screen.
// Charter O-1/O-3: the shell computes and shows no derived or aggregate
// figure — the "today" rows show only raw entered values + a sync badge.
// Charter U-2: launcher buttons are the most-tapped control here and are
// sized at 64dp with >= 8dp separation.

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 64,
  borderRadius: 12,
  border: '1px solid #1d4ed8',
  backgroundColor: '#1d4ed8',
  color: 'white',
  fontWeight: 800,
  fontSize: 18,
  cursor: 'pointer',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '0 18px',
  textAlign: 'left'
}

const syncBadgeStyle: Record<CrewTodaySyncState, React.CSSProperties> = {
  local: { backgroundColor: '#f3f4f6', color: '#374151' },
  pending: { backgroundColor: '#fffbeb', color: '#92400e' },
  synced: { backgroundColor: '#f0fdf4', color: '#166534' },
  error: { backgroundColor: '#fef2f2', color: '#991b1b' }
}

const syncBadgeLabelKey: Record<CrewTodaySyncState, TranslationKey> = {
  local: 'crewMode.home.today.sync.local',
  pending: 'crewMode.home.today.sync.pending',
  synced: 'crewMode.home.today.sync.synced',
  error: 'crewMode.home.today.sync.error'
}

const streamLabelKey = (stream: CrewTodayEntry['stream']): TranslationKey => {
  switch (stream) {
    case 'water':
      return 'crewMode.home.stream.water'
    case 'feed':
      return 'crewMode.home.stream.feed'
    case 'mortality':
      return 'crewMode.home.stream.mortality'
    case 'weightSample':
      return 'crewMode.home.stream.weightSample'
  }
}

const streamIcon: Record<CrewTodayEntry['stream'], string> = {
  water: '💧',
  feed: '🥣',
  mortality: '⚠️',
  weightSample: '⚖️'
}

const EMPTY_TODAY: OperatorTodayEntriesResult = { entries: [], waitingToSyncCount: 0 }

const startOfLocalDayIso = (): string => {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  return start.toISOString()
}

const formatLocalTime = (iso: string): string => {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''
  return when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export default function CrewModeHome() {
  const t = useT()
  const navigate = useNavigate()
  const { farms, farmId: activeFarmId, selectFarm, isLoading } = useCrewModeFarm()
  const farmsLoaded = !isLoading
  const [today, setToday] = useState<OperatorTodayEntriesResult>(EMPTY_TODAY)

  // Server-Side Flag Enforcement v0 follow-up: opportunistically refresh the
  // local flag cache from the real FarmFeatureFlag table whenever this
  // screen opens for a farm — replaces the devtools-console workflow.
  // Best-effort (crewModeFlagSync never throws); `flagsSyncTick` forces
  // `streams` to recompute once the sync resolves.
  const [flagsSyncTick, setFlagsSyncTick] = useState(0)
  useEffect(() => {
    if (!activeFarmId) return
    let cancelled = false
    void syncCrewModeFlagsForFarm(activeFarmId).then(() => {
      if (!cancelled) setFlagsSyncTick((tick) => tick + 1)
    })
    return () => {
      cancelled = true
    }
  }, [activeFarmId])

  const streams = useMemo(() => enabledCrewModeStreams(activeFarmId), [activeFarmId, flagsSyncTick])
  const productionUnitIds = useMemo(() => {
    const farm = farms.find((candidate) => String(candidate.id) === activeFarmId)
    return (farm?.productionUnits ?? []).map((unit) => String(unit.id))
  }, [farms, activeFarmId])
  const unitNames = useMemo(() => {
    const farm = farms.find((candidate) => String(candidate.id) === activeFarmId)
    return Object.fromEntries((farm?.productionUnits ?? []).map((unit) => [String(unit.id), String(unit.name ?? unit.id)]))
  }, [farms, activeFarmId])

  useEffect(() => {
    if (!farmsLoaded) return
    recordCrewEvent('home.opened', { farmId: activeFarmId || undefined })
  }, [farmsLoaded, activeFarmId])

  const loadToday = useCallback(async () => {
    const actorId = getCurrentOperatorActorId()
    if (!activeFarmId || !actorId) {
      setToday(EMPTY_TODAY)
      return
    }

    // Two extra days back so late entries (Ayer / Hace 2 días) entered today
    // are found; the pure filter keeps only today's work.
    const fromRecordedAt = new Date(new Date(startOfLocalDayIso()).getTime() - 2 * 24 * 60 * 60 * 1000).toISOString()
    try {
      await ensureOfflineReady()
      const [feed, mortality, weightSample, outboxEntries] = await Promise.all([
        feedLogApi.list({ farmId: activeFarmId, fromRecordedAt }, { preferLocal: true }),
        mortalityLogApi.list({ farmId: activeFarmId, fromRecordedAt }, { preferLocal: true }),
        weightSampleLogApi.list({ farmId: activeFarmId, fromRecordedAt }, { preferLocal: true }),
        getOfflineDb().entityOutbox.toArray()
      ])

      const waterUnitIdByRowId: Record<string, string> = {}
      const water = productionUnitIds.flatMap((productionUnitId) => {
        const rows = loadWaterTableRows({ farmId: activeFarmId, productionUnitId })
        for (const row of rows) waterUnitIdByRowId[row.id] = productionUnitId
        return rows
      })

      setToday(
        buildOperatorTodayEntries({
          actorId,
          timeZone: farmTimeZone(activeFarmId),
          outbox: outboxEntries.map((entry) => ({
            entityType: entry.entityType,
            entityId: entry.entityId,
            lastError: entry.lastError
          })),
          water,
          feed,
          mortality,
          weightSample,
          unitNames,
          waterUnitIdByRowId
        })
      )
    } catch {
      // Local-first: a failure here means the caches are not ready yet;
      // leave whatever is already shown and let the next trigger retry.
    }
  }, [activeFarmId, productionUnitIds, unitNames])

  useEffect(() => {
    if (!farmsLoaded) return
    void loadToday()
  }, [farmsLoaded, loadToday])

  // PRD §7: refresh on mount and on outbox-draining signals (connectivity
  // returning, the tab regaining focus / visibility) — never on a timer.
  useEffect(() => {
    if (!farmsLoaded) return
    const refresh = () => {
      void loadToday()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    globalThis.addEventListener('online', refresh)
    globalThis.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      globalThis.removeEventListener('online', refresh)
      globalThis.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [farmsLoaded, loadToday])

  const openStream = (route: string) => {
    navigate(route)
  }

  return (
    <OperatorGate>
      <div
        data-testid="crew-mode-home"
        style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <CrewModeFarmBar farmId={activeFarmId} farms={farms} onSelectFarm={selectFarm} />

        <div style={{ fontWeight: 800, fontSize: 18 }}>{t('crewMode.home.prompt')}</div>

        {streams.length === 0 ? (
          <div data-testid="crew-mode-home-empty" style={{ padding: 12, borderRadius: 10, backgroundColor: '#f3f4f6', color: '#374151', fontWeight: 700 }}>
            {t('crewMode.home.noStreams')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {streams.map((stream) => (
              <button
                key={stream.id}
                type="button"
                data-testid={`crew-mode-home-stream-${stream.id}`}
                onClick={() => openStream(stream.route)}
                style={primaryButtonStyle}
              >
                <span aria-hidden="true" style={{ fontSize: 24 }}>{stream.icon}</span>
                <span>{t(stream.labelKey)}</span>
              </button>
            ))}
          </div>
        )}

        {today.entries.length > 0 && (
          <div data-testid="crew-mode-home-today" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#374151' }}>{t('crewMode.home.today.heading')}</div>

            {today.waitingToSyncCount > 0 && (
              <div
                data-testid="crew-mode-home-today-waiting"
                style={{ padding: '6px 10px', borderRadius: 8, fontWeight: 800, ...syncBadgeStyle.pending }}
              >
                {t('crewMode.home.today.waitingToSync', { count: today.waitingToSyncCount })}
              </div>
            )}

            {today.entries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`crew-mode-home-today-row-${entry.id}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid #e5e7eb'
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontWeight: 700 }}>
                    <span aria-hidden="true">{streamIcon[entry.stream]} </span>
                    {t(streamLabelKey(entry.stream))}
                    {' · '}
                    {entry.locationLabel ? (
                      <span data-testid="crew-today-location" style={{ fontWeight: 800 }}>{entry.locationLabel} · </span>
                    ) : null}
                    {entry.fields.map((field) => `${t(field.labelKey)} ${field.value}`.trim()).join(' · ')}
                    {entry.lateEntry ? <span data-testid="crew-today-late"> · {t('crewMode.lateEntry')}</span> : null}
                  </span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{formatLocalTime(entry.recordedAt)}</span>
                </span>
                <span
                  data-testid={`crew-mode-home-today-sync-${entry.id}`}
                  style={{ flexShrink: 0, padding: '4px 8px', borderRadius: 8, fontWeight: 800, fontSize: 12, ...syncBadgeStyle[entry.syncState] }}
                >
                  {t(syncBadgeLabelKey[entry.syncState])}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </OperatorGate>
  )
}
